<template>
    <!-- Modal de Movimentação de Equipamento -->
    <v-dialog
        v-model="visible"
        max-width="600px"
        persistent
    >
        <v-card>

            <v-toolbar color="primary" dark>

                <div 
                    class="d-flex align-center white--text" 
                    style="white-space: nowrap; overflow: visible; margin-left: 10px; font-size: 20px;"
                >
                    <v-icon class="mr-2">mdi-truck</v-icon>
                    Mover Equipamento {{ dadosOrigem.equipamento }}
                </div>
                
                <v-spacer />
                
                <v-btn icon @click="fecharModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-6">

                <v-form ref="formRef" v-model="formValido">
                
                    <!-- Empresa de Destino -->
                    <v-select
                        v-model="dadosMovimentacao.idEmpresaDestino"
                        :items="listaEmpresas"
                        item-title="nome"
                        item-value="id"
                        label="Empresa de Destino *"
                        :loading="carregandoEmpresas"
                        :rules="[v => !!v || 'Selecione a empresa de destino']"
                        clearable
                        @update:model-value="carregarSetoresEmpresa"
                        class="mb-4"
                    />

                    <!-- Setor de Destino -->
                    <v-select
                        v-model="dadosMovimentacao.idSetorDestino"
                        :items="listaSetores"
                        item-title="nome"
                        item-value="id"
                        label="Setor de Destino *"
                        :loading="carregandoSetores"
                        :rules="[v => !!v || 'Selecione o setor de destino']"
                        :disabled="!dadosMovimentacao.idEmpresaDestino"
                        clearable
                        class="mb-4"
                    />

                    <!-- Motivo da Movimentação -->
                    <v-textarea
                        v-model="dadosMovimentacao.motivoMovimento"
                        label="Motivo da Movimentação *"
                        :rules="[
                            v => !!v || 'Informe o motivo da movimentação',
                            v => (v && v.length >= 10) || 'Mínimo 10 caracteres',
                        ]"
                        rows="3"
                        auto-grow
                        counter="500"
                        placeholder="Descreva o motivo da movimentação deste equipamento..."
                        class="mb-4"
                    />

                    <!-- Informações da Origem (somente leitura) -->
                    <v-card variant="outlined" class="mb-6">

                        <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
                            <v-icon size="small" class="mr-2">mdi-information</v-icon>
                            Informações de Origem
                        </v-card-title>

                        <v-card-text>
                            
                            <v-row>
                                <v-col cols="6">
                                <div class="text-caption text-grey">Empresa Origem:</div>
                                <div class="text-body-1">{{ dadosOrigem.nomeEmpresaOrigem }}</div>
                                </v-col>
                                <v-col cols="6">
                                <div class="text-caption text-grey">Setor Origem:</div>
                                <div class="text-body-1">{{ dadosOrigem.nomeSetorOrigem }}</div>
                                </v-col>
                            </v-row>
                            
                            <v-row class="mt-2">
                                <v-col cols="12">
                                <div class="text-caption text-grey">Equipamento:</div>
                                <div class="text-body-1">{{ dadosOrigem.equipamento }}</div>
                                </v-col>
                            </v-row>

                        </v-card-text>

                    </v-card>
                
                </v-form>
            
            </v-card-text>

            <v-card-actions class="pa-4 pt-0">
                
                <v-spacer />
                
                <v-btn
                    variant="outlined"
                    @click="fecharModal"
                    class="mr-2"
                >
                    Cancelar
                </v-btn>
                
                <v-btn
                    color="primary"
                    :loading="movimentando"
                    :disabled="!formValido || movimentando"
                    @click="confirmarMovimentacao"
                >
                    <v-icon start>mdi-check</v-icon>
                    Confirmar Movimentação
                </v-btn>

            </v-card-actions>

        </v-card>

    </v-dialog>

</template>

<script setup>

    // Importa referências do vue
    import { ref, reactive, watch, defineEmits } from 'vue'
    
    // IMporta biblioteca axios http
    import axios from 'axios'

    // Importa store de login
    import { useLoginStore } from '@/stores/loginStore'

    // Importa store de erro
    import { useErroStore } from '@/stores/erroStore'

    // Importa o componente do Snackbar
    import { useSnackbarStore } from '@/stores/snackbar'

    // Monta endereço da api
    const API_BASE_URL = import.meta.env.VITE_API_URL           
            
    // Define a emissão de comando para o formulário que chamou indicando que fechou o modal
    const emit = defineEmits(['movimentacao-concluida', 'fechar'])

    // Importa a store de erros
    const erroStore = useErroStore()

    // Importa a store de chamados
    const loginStore = useLoginStore()

    // Importa a store do snackbar
    const snackbar = useSnackbarStore()
    
    // Variáveis de referência
    const formRef = ref(null)
    const formValido = ref(false)
    const carregandoEmpresas = ref(false)
    const carregandoSetores = ref(false)
    const movimentando = ref(false)

    // Estado do modal
    const visible = ref(false)

    // Dados da origem (preenchidos quando abre o modal)
    const dadosOrigem = reactive({
        idEquipamento: null,
        idEmpresaOrigem: null,
        nomeEmpresaOrigem: '',
        idSetorOrigem: null,
        nomeSetorOrigem: '',
        equipamento: ''
    })
    
    // Dados da movimentação (preenchidos pelo usuário)
    const dadosMovimentacao = reactive({
        idEmpresaDestino: null,
        idSetorDestino: null,
        motivoMovimento: ''
    })

    // Listas para os selects
    const listaEmpresas = ref([])
    const listaSetores = ref([])

    // Limpa dados do modal
    const resetarEstado = () => {

        // Limpa lista de empresas
        listaEmpresas.value = []

        // Limpa lista de setores
        listaSetores.value = []
        
        // Reseta dados da movimentação
        Object.assign(dadosMovimentacao, {
            idEmpresaDestino: null,
            idSetorDestino: null,
            motivoMovimento: ''
        })
        
        // Reseta o formulário
        if (formRef.value) {
            formRef.value.reset()
        }
    }

    // Metodo para abrir formulário modal
    const abrirModal = async (dados) => {

        try {
            
            // Limpa dados do formulário modal
            resetarEstado()

            // Verifica se recebeu dados do formulário ListaEquipamentos
            if (dados) {

                if (dados.operacao == 'mostrar'){

                    const idRegistroMovimentacoes = dados.idRegistroMovimentacoes
                
                } else {
            
                    // Salva os dados recebidos no array dadosOrigem
                    dadosOrigem.idEquipamento = dados.idEquipamento
                    dadosOrigem.idEmpresaOrigem = dados.idEmpresaOrigem
                    dadosOrigem.nomeEmpresaOrigem = dados.nomeEmpresaOrigem
                    dadosOrigem.idSetorOrigem = dados.idSetorOrigem
                    dadosOrigem.nomeSetorOrigem = dados.nomeSetorOrigem
                    dadosOrigem.equipamento = dados.equipamento
                    
                    // Carrega lista de empresas
                    await carregarEmpresas()            
                }
            }
            
            // Abre o modal
            visible.value = true

        } catch (erro) {    
            erroStore.exibirErro(erro)('Erro Ao Preparar Movimentação: ' + erro)
        }
    }

    // Metodo para que fecha o modal
    const fecharModal = () => {
        visible.value = false
        emit('fechar')
    }

    // Carrega lista de empresas
    const carregarEmpresas = async () => {

        try {

            carregandoEmpresas.value = true

            const timestamp = new Date().getTime();

            // Carrega lista de empresas
            const { data } = await axios.get(`${API_BASE_URL}/administracao/carregaListaEmpresas`, {
                params: {
                    textoBuscaEmpresa: '',
                    ordenacaoPor: 'nome',
                    direcaoOrdenacao: 'asc',
                    paginaAtual: 1,
                    itensPorPagina: -1,
                    _t: timestamp 
                },
                headers: {
                    'Authorization': `Bearer ${loginStore.token}`
                }
            })

            // Filtra para não incluir a empresa de origem
            listaEmpresas.value = data.data.map(empresa => ({
                nome: empresa.nomeFantasia,
                id: empresa.idEmpresa
            }))

            // Aponta para a empresa que o usuário fez login
            dadosMovimentacao.idEmpresaDestino = dadosOrigem.idEmpresaOrigem

            // Carrega setores da empresa
            carregarSetoresEmpresa(dadosOrigem.idEmpresaOrigem)

        } catch (erro) {
            
            erroStore.exibirErro('Erro Ao Carregar Lista Empresas: ' + erro)
            listaEmpresas.value = []

        } finally {
            carregandoEmpresas.value = false
        }
    }

    // Carrega setores da empresa selecionada
    const carregarSetoresEmpresa = async (idEmpresa) => {
    
        // Verifica se recebeu o idEmpresa
        if (!idEmpresa) {
        
            // Limpa lista de setores
            listaSetores.value = []    
            dadosMovimentacao.idSetorDestino = null    
            return
        }

        try {
        
            // Sinaliza que está carregando setores
            carregandoSetores.value = true

            // Limpa dados do destino
            dadosMovimentacao.idSetorDestino = null
            dadosMovimentacao.motivoMovimento = ''

            // Carrega lista de setores da empresa selecionada
            const respostaApi = await axios.get(`${API_BASE_URL}/administracao/carregaListaSetoresEmpresa/${idEmpresa}`,
                {
                    params: {
                        textoBuscaEquipamento: '',
                        ordenacaoPor: 'nome',
                        direcaoOrdenacao: 'asc'
                    },
                        headers: {
                        'Authorization': `Bearer ${loginStore.token}`
                    }
                }
            )

            // Filtra o id do setor origem
            listaSetores.value = respostaApi.data.data.map(setor => ({
                
                nome: setor.nome,
                id: setor.id

            })).filter(setor => 
                setor.id !== dadosOrigem.idSetorOrigem
            )

        } catch (erro) {
            
            erroStore.exibirErro('Erro Ao Carregar Setores Empresas: ' + erro)
            listaSetores.value = []

        } finally {
            carregandoSetores.value = false
        }
    }

    // Rotina chamada pelo botão de confirmçao de movimento
    const confirmarMovimentacao = async () => {

        // Valida formulário
        if (!formRef.value) return
    
        // Valida dados do formulário de envio
        const { valid } = await formRef.value.validate()

        // Se não forem válidos sai da rotinr
        if (!valid) return

        try {
            
            // Liga indicação que está movimentando equipamento
            movimentando.value = true

            // TODO: Substitua com seu ID de usuário real
            const idUsuarioLogado = loginStore.idColaborador
        
            // Monta array com os dados de envio
            const dadosEnvio = {
                idEquipamento: dadosOrigem.idEquipamento,
                idEmpresaOrigem: dadosOrigem.idEmpresaOrigem,
                idSetorOrigem: dadosOrigem.idSetorOrigem,
                idEmpresaDestino: dadosMovimentacao.idEmpresaDestino,
                idSetorDestino: dadosMovimentacao.idSetorDestino,
                idQuemMovimentou: idUsuarioLogado,
                motivoMovimento: dadosMovimentacao.motivoMovimento.trim()
            }

            // Chama api de envio
            await axios.post(`${API_BASE_URL}/operacao/moveEquipamento`, dadosEnvio,
                {
                    headers: {
                        'Authorization': `Bearer ${loginStore.token}`,
                        'Content-Type': 'application/json'
                    }
                }
            )

            // Sucesso
            snackbar.trigger('Equipamento Movimentado Com Sucesso!')
        
            // Emite evento de sucesso
            emit('movimentacao-concluida', dadosEnvio)
        
            // Fecha modal
            fecharModal()

        } catch (erro) {
            erroStore.exibirErro('Erro ao movimentar equipamento: ' + erro)
        } finally {
            movimentando.value = false
        }
    }

    // Watch para limpar setor quando empresa mudar
    watch(() => dadosMovimentacao.idEmpresaDestino, (novaEmpresa) => {
    
        if (!novaEmpresa) {
            dadosMovimentacao.idSetorDestino = null
        }
    })

    // Expor métodos para o componente pai
    defineExpose({
        abrirModal,
        fecharModal
    })

</script>

<style scoped>

    .v-card-text {
        padding-top: 20px;
    }

    .v-textarea textarea {
        min-height: 80px;
    }

</style>